import { BaseMonitor } from '@exodus/asset-lib'
import { getAssetAddresses, isRpcBalanceAsset } from '@exodus/ethereum-lib'
import lodash from 'lodash'

import { fromHexToString } from '../number-utils.js'
import { filterEffects, getLogItemsFromServerTx } from './clarity-utils/index.js'
import {
  checkPendingTransactions,
  excludeUnchangedTokenBalances,
  getAllLogItemsByAsset,
  getDeriveDataNeededForTick,
  getDeriveTransactionsToCheck,
} from './monitor-utils/index.js'

const { isEmpty } = lodash

export class ClarityMonitor extends BaseMonitor {
  constructor({ server, config, ...args }) {
    super(args)
    this.config = { GAS_PRICE_FROM_WEBSOCKET: true, ...config }
    this.server = server
    this.getAllLogItemsByAsset = getAllLogItemsByAsset
    this.deriveDataNeededForTick = getDeriveDataNeededForTick(this.aci)
    this.deriveTransactionsToCheck = getDeriveTransactionsToCheck({
      getTxLog: (...args) => this.aci.getTxLog(...args),
    })
    this.addHook('before-start', (...args) => this.beforeStart(...args))
    this.addHook('after-stop', (...args) => this.afterStop(...args))
  }

  setServer(config) {
    const uri = config?.server || this.server.defaultUri

    if (uri === this.server.uri) {
      return
    }

    this.server.setURI(uri)
    this.subscribeWalletAddresses()
    if (this.config.GAS_PRICE_FROM_WEBSOCKET) {
      this.server.connectFee()
    }
  }

  async deriveData({ assetName, walletAccount, tokens }) {
    const { ourWalletAddress, currentAccountState } = await this.deriveDataNeededForTick({
      assetName,
      walletAccount,
    })
    const transactionsToCheck = await this.deriveTransactionsToCheck({
      assetName,
      walletAccount,
      tokens,
      ourWalletAddress,
    })

    return {
      ourWalletAddress,
      currentAccountState,
      ...transactionsToCheck,
    }
  }

  // eslint-disable-next-line no-undef
  async checkPendingTransactions(params) {
    const { pendingTransactionsToCheck, pendingTransactionsGroupedByAddressAndNonce } =
      checkPendingTransactions(params)
    const txsToRemove = []
    const { walletAccount } = params

    const updateTx = (tx, asset, { error, remove }) => {
      if (remove) {
        txsToRemove.push({ tx, assetSource: { asset, walletAccount } })
      } else {
        params.logItemsByAsset[asset].push({
          ...tx,
          dropped: true,
          error,
        })
      }

      // in case this is an ETH fee tx that has associated ERC20 send txs
      const promises = tx.tokens.map(async (assetName) => {
        const tokenTxSet = await this.aci.getTxLog({ assetName, walletAccount })
        if (remove) {
          txsToRemove.push({
            tx: tokenTxSet.get(tx.txId),
            assetSource: { asset: assetName, walletAccount },
          })
        } else if (tokenTxSet && tokenTxSet.has(tx.txId)) {
          params.logItemsByAsset[assetName].push({
            ...tokenTxSet.get(tx.txId),
            error,
            dropped: true,
          })
        }
      })
      return Promise.all(promises)
    }

    for (const { tx, assetName, replaced = false } of Object.values(
      pendingTransactionsGroupedByAddressAndNonce
    )) {
      if (replaced) {
        await updateTx(tx, assetName, { remove: true })
        delete pendingTransactionsToCheck[tx.txId]
      }
    }

    for (const { tx, assetName } of Object.values(pendingTransactionsToCheck)) {
      if (params.refresh) await updateTx(tx, assetName, { remove: true })
      else await updateTx(tx, assetName, { error: 'Dropped' })
    }

    return { txsToRemove }
  }

  async tick({ walletAccount, refresh }) {
    await this.subscribeWalletAddresses()

    const assets = await this.aci.getAssetsForNetwork({ baseAssetName: this.asset.name })
    const tokens = Object.values(assets).filter((asset) => asset.baseAsset.name !== asset.name)
    const tokensByAddress = tokens.reduce((map, token) => {
      const addresses = getAssetAddresses(token)
      for (const address of addresses) map.set(address.toLowerCase(), token)
      return map
    }, new Map())
    const assetName = this.asset.name

    const derivedData = await this.deriveData({ assetName, walletAccount, tokens })
    const response = await this.getHistoryFromServer({ walletAccount, derivedData, refresh })
    const allTxs = [...response.transactions.pending, ...response.transactions.confirmed]
    const hasNewTxs = allTxs.length > 0

    const logItemsByAsset = this.getAllLogItemsByAsset({
      getLogItemsFromServerTx,
      ourWalletAddress: derivedData.ourWalletAddress,
      allTransactionsFromServer: allTxs,
      asset: this.asset,
      tokensByAddress,
      assets,
    })

    const { txsToRemove } = await this.checkPendingTransactions({
      txlist: allTxs,
      walletAccount,
      refresh,
      logItemsByAsset,
      asset: this.asset,
      ...derivedData,
    })

    const accountState = await this.getNewAccountState({
      tokens,
      currentTokenBalances: derivedData.currentAccountState?.tokenBalances,
      ourWalletAddress: derivedData.ourWalletAddress,
    })
    await this.updateAccountState({
      walletAccount,
      newData: { clarityCursor: response.cursor, ...accountState },
    })

    await this.removeFromTxLog(txsToRemove)
    await this.updateTxLogByAsset({ logItemsByAsset, walletAccount, refresh })
    if (refresh || hasNewTxs) {
      const unknownTokenAddresses = this.getUnknownTokenAddresses({
        transactions: allTxs,
        tokensByAddress,
      })
      if (unknownTokenAddresses.length > 0) {
        this.emit('unknown-tokens', unknownTokenAddresses)
      }
    }
  }

  async getNewAccountState({ tokens, currentTokenBalances, ourWalletAddress }) {
    const asset = this.asset
    const newAccountState = {}
    const balances = await this.getBalances({ tokens, ourWalletAddress })
    if (isRpcBalanceAsset(asset)) {
      const balance = balances[asset.name]
      newAccountState.balance = asset.currency.baseUnit(balance)
    }

    const tokenBalancePairs = Object.entries(balances).filter((entry) => entry[0] !== asset.name)
    const tokenBalanceEntries = tokenBalancePairs
      .map((pair) => {
        const token = tokens.find((token) => token.name === pair[0])
        const value = token.currency.baseUnit(pair[1] || 0)
        return [token.name, value]
      })
      .filter(Boolean)

    const tokenBalances = excludeUnchangedTokenBalances(currentTokenBalances, tokenBalanceEntries)
    if (!isEmpty(tokenBalances)) newAccountState.tokenBalances = tokenBalances
    return newAccountState
  }

  async getReceiveAddressesByWalletAccount() {
    const walletAccounts = await this.aci.getWalletAccounts({ assetName: this.asset.name })
    const addressesByAccount = {}
    for (const walletAccount of walletAccounts) {
      addressesByAccount[walletAccount] = await this.aci.getReceiveAddresses({
        assetName: this.asset.name,
        walletAccount,
        useCache: true,
      })
    }

    return addressesByAccount
  }

  async subscribeWalletAddresses() {
    const addressesByWalletAccount = await this.getReceiveAddressesByWalletAccount()
    Object.entries(addressesByWalletAccount).forEach(([walletAccount, addresses]) => {
      const address = String([...addresses][0]).toLowerCase() // Only check m/0/0
      this.server.connectTransactions({ walletAccount, address })
    })
  }

  async getBalances({ tokens, ourWalletAddress }) {
    const batch = {}
    if (isRpcBalanceAsset(this.asset)) {
      const request = this.server.getBalanceRequest(ourWalletAddress)
      batch[this.asset.name] = request
    }

    for (const token of tokens) {
      if (isRpcBalanceAsset(token) && token.contract.address) {
        const request = this.server.balanceOfRequest(ourWalletAddress, token.contract.address)
        batch[token.name] = request
      }
    }

    const pairs = Object.entries(batch)
    if (pairs.length === 0) {
      return {}
    }

    const requests = pairs.map((pair) => pair[1])
    const responses = await this.server.sendBatchRequest(requests)
    const entries = pairs.map((pair, idx) => {
      const balanceHex = responses[idx]
      const name = pair[0]
      const balance = fromHexToString(balanceHex)
      return [name, balance]
    })
    return Object.fromEntries(entries)
  }

  getUnknownTokenAddresses({ transactions, tokensByAddress }) {
    const set = transactions.reduce((acc, txn) => {
      const transfers = filterEffects(txn.effects, 'erc20') || []
      transfers.forEach((transfer) => {
        const addr = transfer.address.toLowerCase()
        if (!tokensByAddress.has(addr)) {
          acc.add(addr)
        }
      })
      return acc
    }, new Set())
    return [...set]
  }

  async updateGasPrice({ gasPrice, baseFeePerGas }) {
    try {
      const feeConfig = { gasPrice: `${fromHexToString(gasPrice).toString()} wei` }
      if (baseFeePerGas) {
        feeConfig.baseFeePerGas = `${fromHexToString(baseFeePerGas)} wei`
      }

      this.logger.debug(
        `Update ${this.asset.name} gas price: ${feeConfig.gasPrice}, baseFeePerGas: ${feeConfig.baseFeePerGas}`
      )
      await this.aci.updateFeeConfig({ assetName: this.asset.name, feeConfig })
    } catch (e) {
      this.logger.warn('error updating gasPrice', e)
    }
  }

  async onTransaction({ walletAccount }) {
    return this.tick({ walletAccount })
  }

  async onFeeUpdated(fee) {
    return this.updateGasPrice(fee)
  }

  async beforeStart() {
    this.listenToServerEvents()
    if (this.config.GAS_PRICE_FROM_WEBSOCKET) {
      this.server.connectFee()
    }

    return this.subscribeWalletAddresses()
  }

  async afterStop() {
    this.server.dispose()
  }

  async getHistoryFromServer({ walletAccount, derivedData, refresh }) {
    const address = derivedData.ourWalletAddress
    const currentCursor = derivedData.currentAccountState?.clarityCursor
    const cursor = currentCursor && !refresh ? currentCursor : null
    return this.server.getAllTransactions({ walletAccount, address, cursor })
  }

  listenToServerEvents() {
    this.server.on('transaction', (...args) => this.onTransaction(...args))
    this.server.on('feeUpdated', (...args) => this.onFeeUpdated(...args))
  }
}
